<script lang="ts">
  import type { Editor } from "@tiptap/core";
  import IconButton from "app/components/+design-system/IconButton/IconButton.svelte";
  import ToolTip from "app/components/+shared/ToolTip/ToolTip.svelte";
  import ColorMenuDropDown from "../../ColorMenu/ColorMenuDropDown.svelte";
  import {
    AI,
    Bold,
    BulletList,
    Code,
    EmojiReaction,
    H1,
    H2,
    Italic,
    Link,
    NumberedList,
    PaintBucket,
    Quote,
    Right,
    TitleCase,
    Toggle,
  } from "$components/+shared/OVIcon";
  import * as linkify from "linkifyjs";

  export let editor: Editor;
  export let aiMode = false;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;

    let url = window.prompt("URL", previousUrl);

    // user clicked cancel
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // const options = { defaultProtocol: "https" };
    // url = linkifyHtml(url, options);
    const links = linkify.find(url);
    url = links[0].href;

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
</script>

<div
  class="z-[1] flex rounded-md border border-gray-300 bg-white p-1 dark:border-white/10 dark:bg-gray-700 dark:text-gray-300"
>
  <IconButton
    a11yLabel="Paragraph"
    on:click={() => editor.chain().focus().clearNodes().run()}
    icon={TitleCase}
    variant="tertiary"
  />
  <IconButton
    a11yLabel="Heading 1"
    on:click={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
    icon={H1}
    variant={editor.isActive("heading", { level: 1 }) ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Heading 2"
    on:click={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
    icon={H2}
    variant={editor.isActive("heading", { level: 2 }) ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Bold"
    on:click={() => editor.chain().focus().toggleBold().run()}
    icon={Bold}
    variant={editor.isActive("bold") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Italic"
    on:click={() => editor.chain().focus().toggleItalic().run()}
    icon={Italic}
    variant={editor.isActive("italic") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Code"
    on:click={() => editor.chain().focus().toggleCode().run()}
    icon={Code}
    variant={editor.isActive("code") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Quote"
    on:click={() => editor.chain().focus().toggleBlockquote().run()}
    icon={Quote}
    variant={editor.isActive("blockquote") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Bullet list"
    on:click={() => editor.chain().focus().toggleBulletList().run()}
    icon={BulletList}
    variant={editor.isActive("bulletList") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Numbered list"
    on:click={() => editor.chain().focus().toggleOrderedList().run()}
    icon={NumberedList}
    variant={editor.isActive("orderedList") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Accordion"
    on:click={() => {
      editor.isActive("details")
        ? editor.chain().focus().unsetDetails().run()
        : editor.chain().focus().setDetails().run();
    }}
    icon={Toggle}
    variant={editor.isActive("details") ? "active" : "tertiary"}
  />
  <IconButton
    a11yLabel="Link"
    on:click={setLink}
    icon={Link}
    variant={editor.isActive("link") ? "active" : "tertiary"}
  />

  {#if aiMode}
    <IconButton
      on:click={() => editor.chain().focus().aiExtend({}).run()}
      icon={Right}
      variant="tertiary"
      a11yLabel={"Extend"}
    />
    <IconButton
      on:click={() => editor.chain().focus().aiEmojify({}).run()}
      icon={EmojiReaction}
      variant="tertiary"
      a11yLabel={"Emojify"}
    />
    <IconButton
      on:click={() => editor.chain().focus().aiTextPrompt({}).run()}
      icon={AI}
      variant="tertiary"
      a11yLabel={"Generate from prompt"}
    />
  {/if}
  <ToolTip clickToTrigger interactive headless>
    <svelte:fragment slot="tip">
      <ColorMenuDropDown {editor} />
    </svelte:fragment>
    <IconButton
      a11yLabel="Color"
      icon={PaintBucket}
      variant={editor.isActive("textColor") || editor.isActive("highlight") ? "active" : "tertiary"}
    />
  </ToolTip>
</div>
